import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Menu, X, HeartPulse, ChevronDown, ShieldCheck } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/resources", label: "Patient Resources" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 bg-[#FAFAF8]/95 backdrop-blur border-b border-[#D9E4E1]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-full bg-[#5E8B7E] flex items-center justify-center">
              <HeartPulse className="w-5 h-5 text-white" />
            </span>
            <span className="font-heading font-semibold text-lg text-slate-800 leading-tight">
              Dr. Prathusha K
              <span className="block text-[11px] font-normal text-[#5E8B7E] -mt-0.5">
                Endogynecologist
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm font-medium transition-colors duration-300 ${
                    isActive
                      ? "bg-[#D9E4E1] text-slate-900"
                      : "text-slate-600 hover:bg-[#F4F7F6] hover:text-slate-900"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <NavLink
              to="/online-consultation"
              data-testid="nav-link-online-consultation"
              className={({ isActive }) =>
                `px-3 py-2 rounded-full text-sm font-medium transition-colors duration-300 ${
                  isActive
                    ? "bg-[#E8DAB2] text-slate-900"
                    : "text-[#2C557E] hover:bg-[#E8DAB2]/40"
                }`
              }
            >
              Online Consultation
            </NavLink>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Button
              data-testid="nav-book-appointment-btn"
              onClick={() => navigate("/booking")}
              className="rounded-full bg-[#5E8B7E] hover:bg-[#4A7065] text-white px-5"
            >
              Book an Appointment
            </Button>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="nav-account-menu"
                    className="flex items-center gap-1 px-3 py-2 rounded-full text-sm font-medium text-slate-700 bg-[#F4F7F6] hover:bg-[#D9E4E1] transition-colors"
                  >
                    {user.name?.split(" ")[0]}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-xl">
                  <DropdownMenuItem
                    data-testid="nav-dashboard-link"
                    onClick={() => navigate(user.role === "admin" ? "/admin" : "/dashboard")}
                  >
                    {user.role === "admin" ? "Admin Dashboard" : "My Dashboard"}
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="nav-logout-btn" onClick={handleLogout}>
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                data-testid="nav-login-btn"
                variant="outline"
                onClick={() => navigate("/login")}
                className="rounded-full border-[#5E8B7E] text-[#5E8B7E] hover:bg-[#D9E4E1]/50"
              >
                Patient Login
              </Button>
            )}
          </div>

          <button
            data-testid="nav-mobile-toggle"
            className="p-2 text-slate-700 ml-2"
            onClick={() => setOpen(!open)}
            aria-label="Open menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="bg-white border-t border-[#D9E4E1] px-4 py-4 space-y-1 max-w-sm ml-auto lg:rounded-bl-2xl lg:shadow-lg">
          {[...navLinks, { to: "/online-consultation", label: "Online Consultation" }].map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="block px-3 py-2 rounded-lg text-slate-700 hover:bg-[#F4F7F6]"
            >
              {l.label}
            </NavLink>
          ))}
          <Button
            onClick={() => {
              setOpen(false);
              navigate("/booking");
            }}
            className="w-full rounded-full bg-[#5E8B7E] hover:bg-[#4A7065] mt-2"
          >
            Book an Appointment
          </Button>
          {user ? (
            <>
              <Button
                variant="outline"
                className="w-full rounded-full mt-1"
                onClick={() => {
                  setOpen(false);
                  navigate(user.role === "admin" ? "/admin" : "/dashboard");
                }}
              >
                {user.role === "admin" ? "Admin Dashboard" : "My Dashboard"}
              </Button>
              <Button variant="ghost" className="w-full rounded-full" onClick={handleLogout}>
                Log out
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              className="w-full rounded-full mt-1"
              onClick={() => {
                setOpen(false);
                navigate("/login");
              }}
            >
              Patient Login
            </Button>
          )}
        </div>
      )}
    </header>
  );
};
